import { Injectable, Inject, InjectionToken, Optional } from '@angular/core';
import { NetworkService, API_BASE_URL } from '@angular-workspace/core';

/**
 * Injection token for leadership API path
 */
export const LEADERSHIP_API_PATH = new InjectionToken<string>('LEADERSHIP_API_PATH');

/** Base URL for hackathon challenge-evaluator (no trailing slash). Apps can override via DI. */
export const CHALLENGE_EVALUATOR_BASE_URL = new InjectionToken<string>(
  'CHALLENGE_EVALUATOR_BASE_URL',
);

const DEFAULT_CHALLENGE_EVALUATOR_BASE_URL =
  'http://bodh.tanuh.ai/challenge-evaluator-backend';

const DEFAULT_BENCHMARK_LEADERBOARD_BASE_URL =
  'https://bodh.tanuh.ai/leaderboard';

/** Base URL for benchmark leaderboard API (no trailing slash). Apps can override via DI. */
export const BENCHMARK_LEADERBOARD_BASE_URL = new InjectionToken<string>(
  'BENCHMARK_LEADERBOARD_BASE_URL',
);

export interface BenchmarkMySubmissionEntry {
  id: number;
  job_id: string;
  dataset_id?: string;
  status?: string;
  num_samples?: number;
  elapsed_seconds?: number;
  metrics?: Record<string, number>;
  error_title?: string;
  error_message?: string;
  submitted_at?: string;
  is_selected?: boolean;
  [key: string]: unknown;
}

export interface BenchmarkMySubmissionsResponse {
  dataset_id: string;
  submissions: BenchmarkMySubmissionEntry[];
}

export function extractBenchmarkMySubmissionsList(
  response: BenchmarkMySubmissionsResponse | null | undefined | BenchmarkMySubmissionEntry[],
): BenchmarkMySubmissionEntry[] {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response.submissions)) {
    return response.submissions;
  }
  const result = (response as unknown as { result?: unknown }).result;
  if (Array.isArray(result)) {
    return result as BenchmarkMySubmissionEntry[];
  }
  const fallback = (response as unknown as { data?: unknown }).data;
  return Array.isArray(fallback) ? (fallback as BenchmarkMySubmissionEntry[]) : [];
}

export interface BenchmarkSelectSubmissionPayload {
  dataset_id: string;
  submission_id: number;
}

/**
 * Interface for leadership member data
 */
export interface LeadershipMember {
  id?: string;
  name: string;
  role: string;
  title?: string;
  rank: number;
  score?: number;
  bio?: string;
  email?: string;
  linkedin?: string;
  avatar?: string;
  category?: 'providers' | 'assets' | 'organisation';
  [key: string]: any;
}

/**
 * Interface for leadership board response
 */
export interface LeadershipBoardResponse {
  members?: LeadershipMember[];
  data?: LeadershipMember[];
  total?: number;
  page?: number;
  size?: number;
  [key: string]: any;
}

export interface HackathonSubmitError extends Error {
  status?: number;
  detail?: string;
  nextAllowedAt?: string;
}

export interface HackathonSubmitResponse {
  _httpStatus: number;
  [key: string]: any;
}

export interface HackathonWorkflowResponse {
  _httpStatus: number;
  [key: string]: any;
}

export interface HackathonRunDetailsResponse {
  _httpStatus: number;
  output?: string;
  [key: string]: any;
}

export interface HackathonRateLimitResponse {
  _httpStatus: number;
  [key: string]: any;
}

/** One row from `my_workflows` (shape varies slightly by problem statement). */
export interface HackathonMyWorkflowRow {
  id?: string;
  databank_id?: string;
  preferred_username?: string;
  challenge_name?: string;
  problem_statement?: string;
  workflow_name?: string;
  status?: string;
  score?: number;
  max_score?: number;
  passed_tests?: number;
  failed_tests?: number;
  total_tests?: number;
  error_message?: string;
  created_at?: string;
  started_at?: string;
  finished_at?: string;
  [key: string]: any;
}

/**
 * Normalizes `my_workflows` JSON (top-level array or wrapped lists) into rows.
 */
export function extractHackathonMyWorkflowsListFromResponse(
  response: HackathonWorkflowResponse | null | undefined,
): HackathonMyWorkflowRow[] {
  if (!response) {
    return [];
  }
  const listKeys = ['items', 'data', 'workflows', 'results', 'result'] as const;
  for (const key of listKeys) {
    const value = response[key];
    if (Array.isArray(value)) {
      return value as HackathonMyWorkflowRow[];
    }
  }
  return [];
}

/**
 * Leaderboard API Service for handling Leaderboard-related API calls
 * This service uses NetworkService to make HTTP requests
 */
@Injectable({
  providedIn: 'root',
})
export class LeadershipApiService {
  private readonly hackathonLeaderboardUrl: string;
  private readonly hackathonSubmitUrl: string;
  private readonly hackathonMyWorkflowsUrl: string;
  private readonly hackathonRunsUrl: string;
  private readonly hackathonRateLimitBaseUrl: string;
  private readonly benchmarkLeaderboardBaseUrl: string;

  constructor(
    private networkService: NetworkService,
    @Inject(API_BASE_URL) private basePathUrl: string,
    @Inject(LEADERSHIP_API_PATH) private leadershipApiPath: string,
    @Optional()
    @Inject(CHALLENGE_EVALUATOR_BASE_URL)
    challengeEvaluatorBaseUrl: string | null,
    @Optional()
    @Inject(BENCHMARK_LEADERBOARD_BASE_URL)
    benchmarkLeaderboardBaseUrl: string | null,
  ) {
    const base = challengeEvaluatorBaseUrl ?? DEFAULT_CHALLENGE_EVALUATOR_BASE_URL;
    this.hackathonLeaderboardUrl = `${base}/leaderboards`;
    this.hackathonSubmitUrl = `${base}/submit`;
    this.hackathonMyWorkflowsUrl = `${base}/my_workflows`;
    this.hackathonRunsUrl = `${base}/runs`;
    this.hackathonRateLimitBaseUrl = `${base}/rate_limit`;
    this.benchmarkLeaderboardBaseUrl =
      benchmarkLeaderboardBaseUrl ?? DEFAULT_BENCHMARK_LEADERBOARD_BASE_URL;
  }

  /**
   * Get leadership board for providers
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with leadership board data for providers
   */
  async getLeadershipForProvider(
    page: number = 1,
    size: number = 10,
  ): Promise<LeadershipBoardResponse> {
    try {
      const url = `${this.basePathUrl}${this.leadershipApiPath}provider?page=${page}&size=${size}`;
      console.log('LeadershipApiService: Calling Provider API with URL:', url);

      const response = await this.networkService.get_api(url, undefined, false);

      console.log(
        'LeadershipApiService: Provider API response received:',
        response,
      );
      return response;
    } catch (error) {
      console.error('Error fetching leadership for providers:', error);
      throw error;
    }
  }

  /**
   * Get leaderboard for assets
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with leaderboard data for assets
   */
  async getLeaderboardForAssets(
    page: number = 1,
    size: number = 10,
  ): Promise<LeadershipBoardResponse> {
    try {
      const url = `${this.basePathUrl}${this.leadershipApiPath}asset?page=${page}&size=${size}`;
      console.log('LeadershipApiService: Calling Assets API with URL:', url);

      const response = await this.networkService.get_api(url, undefined, false);

      console.log(
        'LeadershipApiService: Assets API response received:',
        response,
      );
      return response;
    } catch (error) {
      console.error('Error fetching leaderboard for assets:', error);
      throw error;
    }
  }

  /**
   * Get leaderboard for organisations
   * @param page Page number (default: 1)
   * @param size Page size (default: 10)
   * @returns Promise with leaderboard data for organisations
   */
  async getLeaderboardForOrganisations(
    page: number = 1,
    size: number = 10,
  ): Promise<LeadershipBoardResponse> {
    try {
      const url = `${this.basePathUrl}${this.leadershipApiPath}organization?page=${page}&size=${size}`;
      console.log(
        'LeadershipApiService: Calling Organisation API with URL:',
        url,
      );

      const response = await this.networkService.get_api(url, undefined, false);

      console.log(
        'LeadershipApiService: Organisation API response received:',
        response,
      );
      return response;
    } catch (error) {
      console.error('Error fetching leaderboard for organisations:', error);
      throw error;
    }
  }

  /**
   * Get hackathon leaderboard for a specific problem statement track.
   * @param pvc Problem statement PVC value
   * @returns Promise with leaderboard data
   */
  async getHackathonLeaderboard(
    pvc: string,
    accessToken: string,
  ): Promise<LeadershipBoardResponse> {
    try {
      const body = { pvc };
      console.log(
        'LeadershipApiService: Calling Hackathon leaderboard API with payload:',
        body,
      );

      const response = await this.networkService.post_api(
        this.hackathonLeaderboardUrl,
        body,
        {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        false,
      );

      console.log(
        'LeadershipApiService: Hackathon leaderboard API response received:',
        response,
      );
      return response;
    } catch (error) {
      console.error('Error fetching hackathon leaderboard:', error);
      throw error;
    }
  }

  /**
   * Submit hackathon evaluation for a challenge/problem statement.
   * @param challengeName Human-readable challenge name
   * @param pvc Problem statement PVC value
   * @returns Promise with submit API response
   */
  async submitHackathonEvaluation(
    challengeName: string,
    pvc: string,
    accessToken: string,
  ): Promise<HackathonSubmitResponse> {
    try {
      const body = {
        challenge_name: challengeName,
        pvc,
      };
      console.log(
        'LeadershipApiService: Calling Hackathon submit API with payload:',
        body,
      );
      // Use Fetch API directly to avoid Angular HTTP interceptor header overrides.
      // For this endpoint we must send the exact Authorization token from Postman.
      const response = await fetch(this.hackathonSubmitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      const responseText = await response.text();
      let responseBody: any = {};
      try {
        responseBody = responseText ? JSON.parse(responseText) : {};
      } catch {
        responseBody = { raw: responseText };
      }

      if (!response.ok) {
        const detail = responseBody?.detail || responseText || 'Unknown error';
        const error = new Error(
          `Hackathon submit failed (${response.status}): ${detail}`,
        ) as HackathonSubmitError;
        error.status = response.status;
        error.detail = detail;

        const nextAllowedAtMatch = String(detail).match(
          /next allowed at\s+(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)/i,
        );
        if (nextAllowedAtMatch?.[1]) {
          error.nextAllowedAt = nextAllowedAtMatch[1];
        }

        throw error;
      }

      const normalizedResponse =
        responseBody && typeof responseBody === 'object'
          ? responseBody
          : { value: responseBody };
      const result: HackathonSubmitResponse = {
        ...normalizedResponse,
        _httpStatus: response.status,
      };
      console.log(
        'LeadershipApiService: Hackathon submit API response received:',
        result,
      );
      return result;
    } catch (error) {
      console.error('Error submitting hackathon evaluation:', error);
      throw error;
    }
  }

  async getHackathonMyWorkflows(
    pvc: string,
    accessToken: string,
  ): Promise<HackathonWorkflowResponse> {
    try {
      const body = { pvc };
      console.log(
        'LeadershipApiService: Calling Hackathon my_workflows API with payload:',
        body,
      );

      const response = await fetch(this.hackathonMyWorkflowsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });

      const responseText = await response.text();
      let responseBody: any = {};
      try {
        responseBody = responseText ? JSON.parse(responseText) : {};
      } catch {
        responseBody = { raw: responseText };
      }

      if (!response.ok) {
        const detail = responseBody?.detail || responseText || 'Unknown error';
        const error = new Error(
          `Hackathon my_workflows failed (${response.status}): ${detail}`,
        ) as HackathonSubmitError;
        error.status = response.status;
        error.detail = detail;
        throw error;
      }

      let payloadForMerge: Record<string, unknown>;
      if (Array.isArray(responseBody)) {
        payloadForMerge = { items: responseBody };
      } else if (responseBody && typeof responseBody === 'object') {
        payloadForMerge = responseBody as Record<string, unknown>;
      } else {
        payloadForMerge = { value: responseBody };
      }
      const result: HackathonWorkflowResponse = {
        ...(payloadForMerge as object),
        _httpStatus: response.status,
      };
      console.log(
        'LeadershipApiService: Hackathon my_workflows API response received:',
        result,
      );
      return result;
    } catch (error) {
      console.error('Error fetching hackathon my_workflows:', error);
      throw error;
    }
  }

  async getHackathonRunDetails(
    runId: string,
    accessToken: string,
  ): Promise<HackathonRunDetailsResponse> {
    try {
      const response = await fetch(
        `${this.hackathonRunsUrl}/${encodeURIComponent(runId)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const responseText = await response.text();
      let responseBody: any = {};
      try {
        responseBody = responseText ? JSON.parse(responseText) : {};
      } catch {
        responseBody = { raw: responseText };
      }

      if (!response.ok) {
        const detail =
          responseBody?.detail ||
          responseBody?.error ||
          responseText ||
          'Unknown error';
        const error = new Error(
          `Hackathon run details failed (${response.status}): ${detail}`,
        ) as HackathonSubmitError;
        error.status = response.status;
        error.detail = detail;
        throw error;
      }

      const normalizedResponse =
        responseBody && typeof responseBody === 'object'
          ? responseBody
          : { value: responseBody };
      return {
        ...normalizedResponse,
        _httpStatus: response.status,
      };
    } catch (error) {
      console.error('Error fetching hackathon run details:', error);
      throw error;
    }
  }

  async getBenchmarkMySubmissions(
    datasetId: string,
    accessToken: string,
  ): Promise<BenchmarkMySubmissionsResponse> {
    try {
      const url = `${this.benchmarkLeaderboardBaseUrl}/my-submissions?dataset_id=${encodeURIComponent(datasetId)}`;
      console.log('LeadershipApiService: Calling benchmark my-submissions API:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          accept: '*/*',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const responseText = await response.text();
      let responseBody: Record<string, unknown> = {};
      try {
        responseBody = responseText ? JSON.parse(responseText) : {};
      } catch {
        responseBody = { raw: responseText };
      }

      if (!response.ok) {
        const detail =
          (typeof responseBody['error'] === 'string' && responseBody['error']) ||
          (typeof responseBody['detail'] === 'string' && responseBody['detail']) ||
          responseText ||
          'Unknown error';
        const error = new Error(
          `Benchmark my-submissions failed (${response.status}): ${detail}`,
        ) as HackathonSubmitError;
        error.status = response.status;
        error.detail = String(detail);
        throw error;
      }

      const submissions = Array.isArray(responseBody['submissions'])
        ? (responseBody['submissions'] as BenchmarkMySubmissionEntry[])
        : [];
      const result: BenchmarkMySubmissionsResponse = {
        dataset_id:
          typeof responseBody['dataset_id'] === 'string'
            ? responseBody['dataset_id']
            : datasetId,
        submissions,
      };
      console.log(
        'LeadershipApiService: Benchmark my-submissions API response received:',
        result,
      );
      return result;
    } catch (error) {
      console.error('Error fetching benchmark my-submissions:', error);
      throw error;
    }
  }

  async getBenchmarkSubmissions(
    datasetId: string,
    accessToken: string,
  ): Promise<BenchmarkMySubmissionsResponse> {
    try {
      const url = `${this.benchmarkLeaderboardBaseUrl}/submissions?dataset_id=${encodeURIComponent(datasetId)}`;
      console.log('LeadershipApiService: Calling benchmark submissions API:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          accept: '*/*',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const responseText = await response.text();
      let responseBody: unknown = {};
      try {
        responseBody = responseText ? JSON.parse(responseText) : {};
      } catch {
        responseBody = { raw: responseText };
      }

      if (!response.ok) {
        const errorBody =
          responseBody && typeof responseBody === 'object' && !Array.isArray(responseBody)
            ? (responseBody as Record<string, unknown>)
            : {};
        const detail =
          (typeof errorBody['error'] === 'string' && errorBody['error']) ||
          (typeof errorBody['detail'] === 'string' && errorBody['detail']) ||
          responseText ||
          'Unknown error';
        const error = new Error(
          `Benchmark submissions failed (${response.status}): ${detail}`,
        ) as HackathonSubmitError;
        error.status = response.status;
        error.detail = String(detail);
        throw error;
      }

      let submissions: BenchmarkMySubmissionEntry[] = [];
      if (Array.isArray(responseBody)) {
        submissions = responseBody as BenchmarkMySubmissionEntry[];
      } else if (
        responseBody &&
        typeof responseBody === 'object' &&
        Array.isArray((responseBody as Record<string, unknown>)['submissions'])
      ) {
        submissions = (responseBody as Record<string, unknown>)[
          'submissions'
        ] as BenchmarkMySubmissionEntry[];
      } else if (
        responseBody &&
        typeof responseBody === 'object' &&
        Array.isArray((responseBody as Record<string, unknown>)['result'])
      ) {
        submissions = (responseBody as Record<string, unknown>)[
          'result'
        ] as BenchmarkMySubmissionEntry[];
      } else if (
        responseBody &&
        typeof responseBody === 'object' &&
        Array.isArray((responseBody as Record<string, unknown>)['data'])
      ) {
        submissions = (responseBody as Record<string, unknown>)[
          'data'
        ] as BenchmarkMySubmissionEntry[];
      }

      const result: BenchmarkMySubmissionsResponse = {
        dataset_id:
          responseBody &&
          typeof responseBody === 'object' &&
          !Array.isArray(responseBody) &&
          typeof (responseBody as Record<string, unknown>)['dataset_id'] === 'string'
            ? ((responseBody as Record<string, unknown>)['dataset_id'] as string)
            : datasetId,
        submissions,
      };
      console.log(
        'LeadershipApiService: Benchmark submissions API response received:',
        result,
      );
      return result;
    } catch (error) {
      console.error('Error fetching benchmark submissions:', error);
      throw error;
    }
  }

  async selectBenchmarkSubmission(
    datasetId: string,
    submissionId: number,
    accessToken: string,
  ): Promise<void> {
    try {
      const url = `${this.benchmarkLeaderboardBaseUrl}/select-submission`;
      const payload: BenchmarkSelectSubmissionPayload = {
        dataset_id: datasetId,
        submission_id: submissionId,
      };

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          accept: '*/*',
          'content-type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();
      let responseBody: Record<string, unknown> = {};
      try {
        responseBody = responseText ? JSON.parse(responseText) : {};
      } catch {
        responseBody = { raw: responseText };
      }

      if (!response.ok) {
        const detail =
          (typeof responseBody['error'] === 'string' && responseBody['error']) ||
          (typeof responseBody['detail'] === 'string' && responseBody['detail']) ||
          responseText ||
          'Unknown error';
        const error = new Error(
          `Select leaderboard submission failed (${response.status}): ${detail}`,
        ) as HackathonSubmitError;
        error.status = response.status;
        error.detail = String(detail);
        throw error;
      }
    } catch (error) {
      console.error('Error selecting benchmark leaderboard submission:', error);
      throw error;
    }
  }

  async getHackathonRateLimit(
    problemStatementKey: string,
    accessToken: string,
  ): Promise<HackathonRateLimitResponse> {
    try {
      const response = await fetch(
        `${this.hackathonRateLimitBaseUrl}/${encodeURIComponent(problemStatementKey)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const responseText = await response.text();
      let responseBody: any = {};
      try {
        responseBody = responseText ? JSON.parse(responseText) : {};
      } catch {
        responseBody = { raw: responseText };
      }

      if (!response.ok) {
        const detail =
          responseBody?.detail ||
          responseBody?.error ||
          responseText ||
          'Unknown error';
        const error = new Error(
          `Hackathon rate_limit failed (${response.status}): ${detail}`,
        ) as HackathonSubmitError;
        error.status = response.status;
        error.detail = detail;
        throw error;
      }

      const normalizedResponse =
        responseBody && typeof responseBody === 'object'
          ? responseBody
          : { value: responseBody };
      return {
        ...normalizedResponse,
        _httpStatus: response.status,
      };
    } catch (error) {
      console.error('Error fetching hackathon rate_limit:', error);
      throw error;
    }
  }
}
